import { useEffect } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { queryClient } from '@/lib/queryClient';
import { ApiRequestError } from '@/services/apiClient';
import { authService, type LoginPayload } from '@/services/auth.service';
import { useAuthStore } from '@/store/auth.store';

export function useSessionBootstrap(): void {
  const setUser = useAuthStore((state) => state.setUser);
  const setInitialised = useAuthStore((state) => state.setInitialised);

  useEffect(() => {
    let cancelled = false;

    authService
      .me()
      .then((user) => {
        if (!cancelled) setUser(user);
      })
      .catch(() => {
        if (!cancelled) setInitialised();
      });

    return () => {
      cancelled = true;
    };
  }, [setUser, setInitialised]);
}

export function useSessionExpiryListener(): void {
  const clear = useAuthStore((state) => state.clear);

  useEffect(() => {
    const handler = () => {
      clear();
      queryClient.clear();
    };

    window.addEventListener('searah:session-expired', handler);
    return () => window.removeEventListener('searah:session-expired', handler);
  }, [clear]);
}

export function useLogin() {
  const setUser = useAuthStore((state) => state.setUser);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: LoginPayload) => authService.login(payload),
    onSuccess: (result) => {
      setUser(result.user);
      navigate('/', { replace: true });
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((state) => state.clear);
  const client = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      clear();
      client.clear();
      navigate('/login', { replace: true });
    },
  });
}

export function useUpdateProfile() {
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: authService.updateProfile,
    onSuccess: (user) => setUser(user),
  });
}

export function useUploadAvatar() {
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: authService.uploadAvatar,
    onSuccess: (user) => setUser(user),
  });
}

export function useRemoveAvatar() {
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: authService.removeAvatar,
    onSuccess: (user) => setUser(user),
  });
}

export function useChangePassword() {
  const clear = useAuthStore((state) => state.clear);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: authService.changePassword,
    onSuccess: () => {
      clear();
      navigate('/login?reason=password-changed', { replace: true });
    },
  });
}

export function slugConflictMessage(error: unknown, noun = 'record'): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  if (!error.fieldErrors.some((field) => field.field === 'slug')) return null;

  return `Another ${noun} already uses this web address. The address is made from the title, so give this one a title that differs from the existing ${noun}.`;
}

export function applyFieldErrors<T extends Record<string, unknown>>(
  error: unknown,
  setError: (field: keyof T & string, error: { type: string; message: string }) => void,
): string | null {
  if (!(error instanceof ApiRequestError)) {
    return error instanceof Error ? error.message : 'Something went wrong';
  }

  let matched = false;
  for (const fieldError of error.fieldErrors) {
    setError(fieldError.field as keyof T & string, { type: 'server', message: fieldError.message });
    matched = true;
  }

  return matched ? null : error.message;
}
